import React, {
  useCallback, useEffect, useRef, useState,
} from "react"
import { isEmpty } from "lodash-es"
import { useModal } from "hooks"
import * as API from "services/api"
import { useHistoricalAccessToolContext } from "contexts/HistoricalAccessToolContext"
import { PrimaryButton, TertiaryButton } from "shared/buttons"
import Modal, { DEFAULT_MODAL_STYLE, ModalHeader } from "shared/Modal"
import { errorToast, successToast } from "shared/toast"
import ActivityIndicator from "shared/ActivityIndicator"
import HistoricalAccessToolPreviewList from "shared/HistoricalAccessTool/HistoricalAccessToolPreviewList"

const PREVIEW_DONE_STATUS = "done"
const PREVIEW_FAILED_STATUS = "failed"

const HistoricalAccessToolRemoveAccessPreview = () => {
  const { modalIsOpen, openModal, closeModal } = useModal()
  const [formSubmissionCounts, setFormSubmissionCounts] = useState([])
  const [previewStatus, setPreviewStatus] = useState(null)
  const pollingIntervalRef = useRef(null)

  const {
    allRequiredRemoveAccessInstructionsDataEntered,
    earliestSubmissionDate,
    latestSubmissionDate,
    reportableFieldLocationAnswerFilters,
    resetHistoricalAccessToolData,
    selectedFormsForSubmittingIds,
    userId,
  } = useHistoricalAccessToolContext()

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  const startPolling = useCallback((previewId) => {
    pollingIntervalRef.current = setInterval(async () => {
      const response = await API.getHistoricalAccessToolRemoveAccessPreview({ previewId })

      if (!response.ok) {
        stopPolling()
        setPreviewStatus(PREVIEW_FAILED_STATUS)
        errorToast("Something went wrong.  Unable to generate preview.")
        return
      }

      const { status, formSubmissionCounts: counts } = response.data
      setPreviewStatus(status)

      if (status === PREVIEW_DONE_STATUS) {
        stopPolling()
        setFormSubmissionCounts(counts || [])
      } else if (status === PREVIEW_FAILED_STATUS) {
        stopPolling()
        errorToast("Something went wrong.  Unable to generate preview.")
      }
    }, 2000)
  }, [stopPolling])

  const loadFormSubmissionCounts = async () => {
    const response = await API.historicalAccessToolRemoveAccessPreviews({
      earliestSubmissionDate,
      formsForSubmittingIds: selectedFormsForSubmittingIds,
      latestSubmissionDate,
      reportableFieldLocationAnswerFilters,
      userId,
    })

    if (response.ok) {
      const { id: previewId, status } = response.data
      setPreviewStatus(status)
      startPolling(previewId)
      return true
    }

    errorToast("Something went wrong. Unable to generate preview.")
    return false
  }

  const removeAccess = async () => {
    const response = await API.historicalAccessToolRemoveAccess({
      earliestSubmissionDate,
      formsForSubmittingIds: selectedFormsForSubmittingIds,
      latestSubmissionDate,
      reportableFieldLocationAnswerFilters,
      userId,
    })

    if (response.ok) {
      successToast("Remove access request received.  We'll email you with results once your request has been processed.")
      resetHistoricalAccessToolData()
      closeModal()
      setFormSubmissionCounts([])
      setPreviewStatus(null)
    } else {
      errorToast("Something went wrong.  Unable to remove access.")
    }
  }

  const generatePreview = async () => {
    const success = await loadFormSubmissionCounts()
    if (success) openModal()
  }

  const cancel = () => {
    stopPolling()
    closeModal()
    setFormSubmissionCounts([])
    setPreviewStatus(null)
  }

  const previewIsLoading = previewStatus && previewStatus !== PREVIEW_DONE_STATUS && previewStatus !== PREVIEW_FAILED_STATUS

  return (
    <>
      <div className="flex justify-center">
        <PrimaryButton text="Preview" disabled={!allRequiredRemoveAccessInstructionsDataEntered} onClick={generatePreview} />
      </div>
      <Modal isOpen={modalIsOpen} style={DEFAULT_MODAL_STYLE}>
        <ModalHeader
          closeModal={cancel}
          heading="Remove Access Preview"
        />
        {previewIsLoading && (
          <div className="flex justify-center mb-4">
            <ActivityIndicator />
          </div>
        )}
        {!previewIsLoading && !isEmpty(formSubmissionCounts) && (
          <HistoricalAccessToolPreviewList formSubmissionCounts={formSubmissionCounts} className="mb-4" />
        )}
        <p className="text-sm text-gray mb-8 text-center">
          If the user is setup to be granted submission access based on certain forms, workflows, groups, etc.,
          they may still gain access to future submissions both during and after this access removal request is processed.
        </p>
        <div className="flex justify-center gap-4">
          <TertiaryButton text="Cancel" onClick={cancel} />
          <PrimaryButton text="Confirm" onClick={removeAccess} disabled={previewStatus !== PREVIEW_DONE_STATUS} />
        </div>
      </Modal>
    </>
  )
}

export default HistoricalAccessToolRemoveAccessPreview
